import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import useOfferUpgrade from '@shared/v2/hooks/useOfferUpgrade';
import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import useToggle from '@shared/v2/hooks/useToggle';

import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
// Hooks
import useDestination from '@/hooks/useDestination';
import usePropertyContent from '@/hooks/usePropertyContent';
import CampaignAppliedNotification from '@/v2/components/pages/PackageViewV2/components/CampaignAppliedNotification';
import OfferModal from '@/v2/components/pages/PackageViewV2/components/OfferModal';
import CtaButton from '@/v2/components/pages/PackageViewV2/components/RoomOptions/components/SearchPanel/components/CtaButton';
import { useFlightModal } from '@/v2/components/pages/PackageViewV2/contexts/FlightModalContext';
import { useRoomOptionsData } from '@/v2/components/pages/PackageViewV2/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import {
  DateRangeInput,
  Dates,
  TravellersInput,
} from '@/v2/components/ui/Input';
import { useCampaign } from '@/v2/contexts/campaign';
// Context hooks
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { usePackagePageV3 } from '@/v2/hooks/featureFlags/packagePageV3/usePackagePageV3';
import { Travellers } from '@/v2/services/sanity/types/api/response';
// Types
import { UI } from '@/v2/types';
import { dataLayer } from '@/v2/utils/dataLayer';
import getEcommerceDetailedItem from '@/v2/utils/dataLayer/eventDataHelpers/getEcommerceDetailedItemEventData';
import {
  handleDateDataLayerEvent,
  handleOccupantsDataLayerEvent,
} from '@/v2/utils/dataLayer/searchEvents/changeHandlers';
import AlternativeOffer from './components/AlternativeOffer';
import PackageOptionDetails from './components/PackageOptionDetails';
// Components
import SelectedRoomPanel from './components/SelectedRoomPanel';

const Panel = styled(Flex)`
  background: ${themeGet('components.searchPanel.backgroundColor')};
  flex-direction: column;

  box-shadow: ${themeGet('shadows.elevation6')};
`;

const FormSection = styled(Flex)`
  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }
`;

const SearchPanel = () => {
  const isPackagePageV3Enabled = usePackagePageV3();
  const shouldUseNewEvents = useGA4Events();
  const { searchQuery } = useSearchQuery();
  const {
    isCalendarOpen,
    openCalendar,
    closeCalendar,
    isTravellersOpen,
    closeTravellers,
    openTravellers,
    calendarRoomId,
    setCalendarRoomId,
  } = useSearchPanelControls();

  const breakpoints = useBreakpoints();
  const smallScreen = isScreen(breakpoints)('xs', 'sm');

  const {
    value: isUpgradeModalOpen,
    setOn: openUpgradeModal,
    setOff: closeUpgradeModal,
  } = useToggle(false);

  const [showErrors, setShowErrors] = useState(false);

  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer | undefined = useProcuredOffer();
  const {
    selectedRoomOption,
    selectedPackageOption,
    roomOptions,
    isPreSearch,
    isLoading,
    packageOptions,
  } = useRoomOptionsData() || {};
  const upgradeOffer = useOfferUpgrade({
    inclusionSets: procuredOffer?.inclusionSets,
    selectedRoomOption,
  });

  const campaign = useCampaign();
  const { openFlightModal } = useFlightModal({
    roomId: selectedRoomOption?.roomType.id,
  });

  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const {
    data: {
      departureDate,
      returnDate,
      originCode,
      adults,
      children,
      infants,
      packageOption,
    },
    handleChangeData,
    handleSearch,
    error,
  } = usePackageSearch();

  const dates = { departureDate, returnDate };
  const travellers = { adults, children, infants };

  const blackoutDates = useMemo(
    () => destination && originCode && destination.blackoutDates[originCode],
    [destination, originCode],
  );

  const minimumNights = useMemo(
    () => destination && destination.searchConfig.minimumNights,
    [destination],
  );

  const disabled = !originCode || !packageOption;

  const handleDataSubmit = (selectedRoomId?: string) => {
    if (Object.values(error).every((v) => !v)) {
      if (shouldUseNewEvents) {
        handleDateDataLayerEvent({}, dates);
      }

      setCalendarRoomId(selectedRoomId);
      handleSearch({ room: selectedRoomId });
    } else {
      setShowErrors(true);
    }
  };

  const handleDatesChange = useMemo(
    () => (dates?: Dates) => {
      setShowErrors(false);

      const departing =
        dates?.departureDate instanceof Date
          ? dates?.departureDate.toString()
          : dates?.departureDate;
      const returning =
        dates?.returnDate instanceof Date
          ? dates?.returnDate.toString()
          : dates?.returnDate;

      handleChangeData({
        departureDate: departing,
        returnDate: returning,
      });
    },
    [handleChangeData],
  );

  const handleTravellersChange = useMemo(
    () => (travellers: Travellers) => {
      setShowErrors(false);

      const {
        adults: newAdults,
        children: newChildren,
        infants: newInfants,
      } = travellers;

      if (shouldUseNewEvents) {
        handleOccupantsDataLayerEvent(
          { adults, children, infants },
          travellers,
        );
      }

      handleChangeData({
        adults: newAdults,
        children: newChildren,
        infants: newInfants,
      });
    },
    [adults, children, handleChangeData, infants, shouldUseNewEvents],
  );

  const handleReserve = () => {
    if (shouldUseNewEvents) {
      const item = getEcommerceDetailedItem({
        price: Number(
          selectedRoomOption?.matchingOffer?.charges?.total?.amount,
        ),
        property: { propertyContent, procuredOffer: procuredOffer || null },
        originCode: searchQuery?.originCode,
        travellers: {
          adults: searchQuery?.adults ?? 2,
          children: searchQuery?.children ?? 0,
          infants: searchQuery?.infants ?? 0,
        },
        travelDateRange: {
          start: searchQuery?.departureDate,
          end: searchQuery?.returnDate,
        },
        availableOfferCount: (packageOptions ?? []).length + 1,
        availableRoomCount: (roomOptions?.main || []).filter(
          ({ matchingLeadInOffer, matchingOffer }) =>
            isPreSearch ? !!matchingLeadInOffer : !!matchingOffer,
        )?.length,
        leadInRoom: selectedRoomOption ?? (roomOptions?.main || [])[0],
        isPreSearch: false,
      });

      dataLayer.addToCartEvent({ item });
    }
    const isPoOffer = !!selectedRoomOption?.matchingOffer?.leadInOffer;
    if (isPoOffer && upgradeOffer) {
      openUpgradeModal();
    } else {
      openFlightModal({
        offerId: selectedRoomOption?.matchingOffer?.hotelOffer.id,
      });
    }
  };

  const handleUpgrade = async () => {
    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: 'Upgrade package',
      });
    }
    await openFlightModal({
      offerId: upgradeOffer?.hotelOffer.id,
    });
    closeUpgradeModal();
  };

  const handleOpenFlightModal = async () => {
    await openFlightModal({
      offerId: selectedRoomOption?.matchingOffer?.hotelOffer.id,
    });
    closeUpgradeModal();
  };

  const roomTypeOptions = roomOptions?.main?.map((roomOption) => ({
    value: roomOption.roomType.id,
    text: roomOption.roomType.name,
  }));

  const isPackageDetailsPresearchEnabled =
    isPackagePageV3Enabled &&
    isPreSearch &&
    selectedPackageOption &&
    !isLoading;

  const hasMatchingOffer = roomOptions?.main?.some(
    (option) =>
      option.roomType.id === selectedRoomOption?.roomType.id &&
      option.matchingOffer,
  );

  const isSelectedRoomHasNoMatchingOffer =
    selectedRoomOption && !hasMatchingOffer;

  return (
    <>
      <Box maxWidth="424px">
        <Panel
          p="4"
          pb="6"
          mb="6"
          boxShadow="elevation1"
          borderColor="greys.alto"
          borderRadius="sm"
          border="1"
          height="min-content"
        >
          <FormSection flexDirection="column" gap="16px">
            <DateRangeInput
              isOpen={isCalendarOpen && !smallScreen}
              onClose={closeCalendar}
              onOpen={openCalendar}
              dates={dates}
              blackoutDates={blackoutDates}
              minimumNights={minimumNights}
              onDataChange={handleDatesChange}
              onDataSubmit={handleDataSubmit}
              campaign={campaign}
              error={showErrors ? error : undefined}
              disabled={disabled}
              roomId={calendarRoomId || searchQuery?.room}
              roomTypes={roomTypeOptions}
            />
            <TravellersInput
              isOpen={isTravellersOpen && !smallScreen}
              onOpen={openTravellers}
              onClose={closeTravellers}
              travellers={travellers}
              onDataChange={handleTravellersChange}
              error={showErrors ? error : undefined}
              disabled={disabled}
            />
            <CtaButton
              hasSelection={!!(selectedRoomOption && hasMatchingOffer)}
              onReserve={handleReserve}
              onCheckAvailability={openCalendar}
              isDisabled={disabled}
            />
          </FormSection>
          <CampaignAppliedNotification mb="3" />
          {isPackageDetailsPresearchEnabled && (
            <PackageOptionDetails
              selectedPackageOption={selectedPackageOption}
              limitInclusions={true}
            />
          )}
          {selectedRoomOption && !isLoading && (
            <SelectedRoomPanel
              upgradeOffer={upgradeOffer}
              selectedPackageOption={selectedPackageOption}
              selectedRoomOption={selectedRoomOption}
            />
          )}
          {(!selectedRoomOption || isSelectedRoomHasNoMatchingOffer) &&
            !isPreSearch &&
            !isLoading && <AlternativeOffer />}
        </Panel>
      </Box>
      <OfferModal.Upgrade
        isOpen={isUpgradeModalOpen}
        onCancel={handleOpenFlightModal}
        onSubmit={handleUpgrade}
        onClose={closeUpgradeModal}
        propertyContent={propertyContent}
        procuredOffer={procuredOffer}
        offer={upgradeOffer}
        roomType={selectedRoomOption?.roomType}
      />
    </>
  );
};

export default SearchPanel;
